import axios from "axios";
import { createStore, applyMiddleware } from "redux";
import { composeWithDevTools } from "redux-devtools-extension/developmentOnly";
import thunk from "redux-thunk";
import moment from "moment";
import { loadTranslations, setLocale, syncTranslationWithStore } from "react-redux-i18n";

import rootReducer from "./reducers";
import * as translations from "./locale/";
import * as translationsGoCharge from "./locale/gocharge/";

import { FETCH_LANGUAGES, FETCH_TRANSLATION } from "./actions/types";


const LANGUAGE_TRANSLATION = `/api/public/language/v2/components/evio/legal/`;
const ALLOWED_LANGUAGES = `/api/public/language/v2/languagesAllowed`;
const FALLBACK_LANGUAGE = "en_GB";

// Normalize locale formats like en_GB or en-GB to short form (en).
const toShortLanguage = (languageCode) =>
  (languageCode || "")
    .toLowerCase()
    .split(/[-_]/)[0];

// Build a unique list of allowed language codes returned by the API.
const getAllowedLanguageCodes = (allowedLanguages = []) => {
  const normalized = allowedLanguages.filter(Boolean);

  return Array.from(new Set(normalized));
};

// Fetch one locale from Weblate using the selected API language code.
const fetchTranslationFile = async (languageCode) => {
  const localeKey = toShortLanguage(languageCode);
  const requestTs = Date.now();
  const response = await axios.get(`${LANGUAGE_TRANSLATION + languageCode}/file`, {
    params: {
      metadata: false,
      _ts: requestTs,
    },
  });
  console.info(`Fetched translation from Weblate: ${languageCode}`);

  const payload = {
    localeKey,
    translation: response.data.translation || {},
    translationHash: response.data.language.translationHash,
  };

  return payload;
};

// Resolve the effective locale from URL + allowed languages + fallback.
const resolveLanguageCode = (allowedLanguages = []) => {

/*  reading locale from URL path (for example /en/...)
    matching that against allowed languages
    fallback to configured fallback language if needed */
  
  const allowedCodes = getAllowedLanguageCodes(allowedLanguages);

  const pathLanguage = (window.location.pathname.split("/")[1] || "").toLowerCase();
  const preferredShort = pathLanguage || toShortLanguage(FALLBACK_LANGUAGE);

  const preferred = allowedCodes.find(
    (code) => toShortLanguage(code) === preferredShort
  );

  const fallback = allowedCodes.find(
    (code) => toShortLanguage(code) === toShortLanguage(FALLBACK_LANGUAGE)
  );

  const selectedLanguageCode =
    preferred || fallback || allowedCodes[0] || FALLBACK_LANGUAGE;

  return selectedLanguageCode;
};

// Read locale requested in route (e.g., /en/terms&conditions).
const getPathLocale = () =>
  toShortLanguage(window.location.pathname.split("/")[1] || "");



const middleware = [thunk];

const saveState = state => {
  try {
    // Convert the state to a JSON string
    const serialisedState = JSON.stringify(state);

    // Save the serialised state to localStorage against the key 'app_state'
    window.localStorage.setItem("app_state", serialisedState);
  } catch (err) {
    // Log errors here, or ignore
    console.log(err);
  }
};

const loadState = () => {
  try {
    // Load the data saved in localStorage, against the key 'app_state'
    const serialisedState = window.localStorage.getItem("app_state");

    // Passing undefined to createStore will result in our app getting the default state
    // If no data is saved, return undefined
    if (!serialisedState) return undefined;

    // De-serialise the saved state, and return it.
    return JSON.parse(serialisedState);
  } catch (err) {
    // Return undefined if localStorage is not available,
    // or data could not be de-serialised,
    // or there was some other error
    return undefined;
  }
};

// Fetch and store allowed languages from backend configuration.
export const fetchAllowedLanguagesOnly = () => (dispatch) => {
  /* This function its used to fetch the default allowed languages and then saving in the local storage  */
  return axios
    .get(ALLOWED_LANGUAGES)
    .then((res) => {
      if (res.status === 200) {
        const languages = res.data.languagesAllowed.languages;
        dispatch({
          type: FETCH_LANGUAGES,
          payload: languages,
        });

        return languages;
      }

      return [];
    })
    .catch((err) => {
      if (err) console.log(err);
      return [];
    });
};

// Fetch selected locale translation, merge into catalog, and activate locale.
export const fetchTranslationFromWeblate = () => async (dispatch, getState) => {
  const allowedLanguages = await dispatch(fetchAllowedLanguagesOnly());
  const pathLocaleKey = getPathLocale();
  const selectedLanguageCode = resolveLanguageCode(allowedLanguages);
  const localeToLoad = selectedLanguageCode;
  const selectedLocaleKey = pathLocaleKey || toShortLanguage(selectedLanguageCode);

  const existingTranslations = getState().i18n.translations || {};
  const mergedTranslations = { ...existingTranslations };
  let selectedTranslation = null;

  try {
    const { localeKey, translation, translationHash } = await fetchTranslationFile(localeToLoad);

    mergedTranslations[localeKey] = {
      ...(mergedTranslations[localeKey] || {}),
      ...translation,
    };

    if (localeKey === selectedLocaleKey) {
      selectedTranslation = {
        translation,
        translationHash,
      };
    }
  } catch (err) {
    console.warn("Weblate translation fetch failed:", err);
  }

  dispatch(loadTranslations(mergedTranslations));

  if (selectedTranslation) {
    dispatch({
      type: FETCH_TRANSLATION,
      payload: selectedTranslation,
    });
  }

  moment.locale(selectedLocaleKey);
  dispatch(setLocale(selectedLocaleKey));
};

// Trigger Weblate loading once store bootstrapping is ready.
const loadFromWeblate = async () => {
  await store.dispatch(fetchTranslationFromWeblate());
}

// Merge bundled catalogs so both generic and goCharge-specific keys are present.
const mergeBundledTranslations = (baseCatalog = {}, goChargeCatalog = {}) => {
  const locales = new Set([
    ...Object.keys(baseCatalog),
    ...Object.keys(goChargeCatalog),
  ]);

  const merged = {};

  locales.forEach((locale) => {
    merged[locale] = {
      ...(baseCatalog[locale] || {}),
      ...(goChargeCatalog[locale] || {}),
    };
  });

  return merged;
};

// Load bundled translation catalogs as a local fallback per brand.
const pickLanguage = async () => {
  const bundledTranslations = mergeBundledTranslations(
    translations,
    translationsGoCharge
  );

  // Keep cached translations from localStorage so UI has data before Weblate returns.
  const cachedTranslations = store.getState().i18n?.translations || {};
  const startupTranslations = mergeBundledTranslations(
    bundledTranslations,
    cachedTranslations
  );

  const cachedLocale = toShortLanguage(store.getState().i18n?.locale);
  const startupLocale = getPathLocale() || cachedLocale || toShortLanguage(FALLBACK_LANGUAGE);

  store.dispatch(loadTranslations(startupTranslations));
  moment.locale(startupLocale);
  store.dispatch(setLocale(startupLocale));
}

const oldState = loadState();

const store = createStore(
  rootReducer,
  oldState,
  composeWithDevTools(applyMiddleware(...middleware))
);

store.subscribe(() => {
  saveState(store.getState());
});

syncTranslationWithStore(store);

pickLanguage();
loadFromWeblate();

export default store;
